<script setup lang="ts">
/**
 * The busiest processes now (server/processes.ts), under the CPU or RAM chart.
 * "By program" adds up every process of one name — a dozen node processes are
 * one question. Their memory then counts shared pages once per process, so it
 * can come out above what the box has.
 *
 * A process's row opens it in full under itself (ProcessDetail); a program's
 * does not, being many processes.
 */
import { computed, onMounted, ref } from "vue";
import { api } from "../lib/api.js";
import { bytesLabel } from "../lib/bytes.js";
import ProcessDetail from "./ProcessDetail.vue";
import type { ProcessRow } from "@shared/types.js";

const props = defineProps<{ sort: "cpu" | "ram" }>();

const rows = ref<ProcessRow[] | null>(null);
const loading = ref(false);
const error = ref("");
const byProgram = ref(false);
/** The process opened under its row; it stays open across a Refresh while it is still listed. */
const openPid = ref<number | null>(null);

function toggle(l: Line): void {
  if (l.pid === null) return;
  openPid.value = openPid.value === l.pid ? null : l.pid;
}

async function load(): Promise<void> {
  loading.value = true;
  try {
    rows.value = (await api.get<{ processes: ProcessRow[] }>(`/api/system-stats/processes?sort=${props.sort}`)).processes;
    error.value = "";
  } catch (err) {
    error.value = (err as Error).message;
  } finally {
    loading.value = false;
  }
}

interface Line {
  key: string;
  /** Null for a program's row. */
  pid: number | null;
  name: string;
  detail: string;
  title: string;
  user: string;
  cpu: number;
  rssBytes: number;
}

const lines = computed<Line[]>(() => {
  const r = rows.value ?? [];
  if (!byProgram.value) {
    return r.map((p) => ({ key: String(p.pid), pid: p.pid, name: p.name, detail: p.command, title: `pid ${p.pid}: ${p.command}`, user: p.user, cpu: p.cpu, rssBytes: p.rssBytes }));
  }
  const groups = new Map<string, Line & { count: number; users: Set<string> }>();
  for (const p of r) {
    const g = groups.get(p.name) ?? { key: p.name, pid: null, name: p.name, detail: "", title: "", user: "", cpu: 0, rssBytes: 0, count: 0, users: new Set<string>() };
    g.cpu += p.cpu;
    g.rssBytes += p.rssBytes;
    g.count++;
    g.users.add(p.user);
    groups.set(p.name, g);
  }
  return [...groups.values()]
    .map((g) => ({ ...g, detail: `${g.count} process${g.count === 1 ? "" : "es"}`, title: g.name, user: [...g.users].join(", ") }))
    .sort((a, b) => (props.sort === "cpu" ? b.cpu - a.cpu : b.rssBytes - a.rssBytes));
});

const cpuLabel = (n: number): string => (n < 0.5 ? "0%" : `${Math.round(n)}%`);

onMounted(() => void load());
</script>

<template>
  <div class="stats-detail">
    <div class="stats-detail-head">
      <span>{{ sort === "cpu" ? "Busiest now" : "Most memory" }}</span>
      <span class="stats-sub">CPU is % of one core over the last second</span>
      <span style="flex: 1"></span>
      <label class="stats-toggle"><input v-model="byProgram" type="checkbox" /> by program</label>
      <button class="btn ghost" type="button" :disabled="loading" @click="load">{{ loading ? "Reading…" : "Refresh" }}</button>
    </div>
    <p v-if="error" class="field-problem">{{ error }}</p>
    <p v-else-if="!rows" class="stats-sub">Reading…</p>
    <p v-if="byProgram" class="stats-sub stats-detail-note">Of the {{ rows?.length ?? 0 }} busiest processes only.</p>
    <table v-if="rows" class="stats-table">
      <thead>
        <tr>
          <th>{{ byProgram ? "Program" : "Process" }}</th>
          <th>User</th>
          <th class="num" :class="{ on: sort === 'cpu' }">CPU</th>
          <th class="num" :class="{ on: sort === 'ram' }">RAM</th>
        </tr>
      </thead>
      <tbody>
        <template v-for="l in lines" :key="l.key">
          <tr
            :title="l.title"
            :class="{ 'proc-row': l.pid !== null, open: l.pid !== null && l.pid === openPid }"
            :tabindex="l.pid !== null ? 0 : undefined"
            :aria-expanded="l.pid !== null ? l.pid === openPid : undefined"
            @click="toggle(l)"
            @keydown.enter.prevent="toggle(l)"
            @keydown.space.prevent="toggle(l)"
          >
            <td class="stats-proc">
              <strong>{{ l.name }}</strong>
              <span class="stats-cmd">{{ l.detail }}</span>
            </td>
            <td class="stats-user">{{ l.user }}</td>
            <td class="num">{{ cpuLabel(l.cpu) }}</td>
            <td class="num">{{ bytesLabel(l.rssBytes) }}</td>
          </tr>
          <tr v-if="l.pid !== null && l.pid === openPid" class="proc-detail-row">
            <td colspan="4"><ProcessDetail :pid="l.pid" /></td>
          </tr>
        </template>
      </tbody>
    </table>
  </div>
</template>
